/**
 * CSV Citizenship Import/Export profiles — named sets on connector type `csv`
 * with citizenship metadata (source, defaults, schedule, export filter).
 */

import {
  loadFieldMappingConfig,
  saveFieldMappingConfig,
  type FieldMappingEntry,
  type FieldMappingSet,
} from '../fieldMappingRegistry';
import { getSupabaseClient } from '../supabaseClient';
import type {
  CsvCitizenshipKind,
  CsvCitizenshipProfileMeta,
} from './csvCitizenshipTypes';
import { CSV_CONNECTOR_TYPE } from './csvMappingProfiles';

function newId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  return `csv_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

export interface CsvCitizenshipProfile {
  set: FieldMappingSet;
  meta: CsvCitizenshipProfileMeta;
}

function asProfile(set: FieldMappingSet): CsvCitizenshipProfile | null {
  if (!set.citizenship?.kind) return null;
  return { set, meta: set.citizenship };
}

export async function listCsvCitizenshipProfiles(
  workspaceId: string,
  kind?: CsvCitizenshipKind,
): Promise<CsvCitizenshipProfile[]> {
  const config = await loadFieldMappingConfig(workspaceId, CSV_CONNECTOR_TYPE);
  return config.sets
    .map(asProfile)
    .filter((p): p is CsvCitizenshipProfile => Boolean(p))
    .filter((p) => (kind ? p.meta.kind === kind : true));
}

export async function getCsvCitizenshipProfile(
  workspaceId: string,
  profileId: string,
): Promise<CsvCitizenshipProfile | null> {
  const config = await loadFieldMappingConfig(workspaceId, CSV_CONNECTOR_TYPE);
  const set = config.sets.find((s) => s.id === profileId);
  return set ? asProfile(set) : null;
}

export async function saveCsvCitizenshipProfile(params: {
  workspaceId: string;
  name: string;
  description?: string;
  meta: CsvCitizenshipProfileMeta;
  entries: FieldMappingEntry[];
  setId?: string;
}): Promise<{ profile: CsvCitizenshipProfile; error: string | null }> {
  const config = await loadFieldMappingConfig(params.workspaceId, CSV_CONNECTOR_TYPE);
  const now = new Date().toISOString();
  const existingIdx = params.setId
    ? config.sets.findIndex((s) => s.id === params.setId)
    : -1;

  const set: FieldMappingSet = {
    id: existingIdx >= 0 ? config.sets[existingIdx].id : newId(),
    connectorType: CSV_CONNECTOR_TYPE,
    name: params.name.trim() || 'Untitled profile',
    description: params.description ?? '',
    entries: params.entries,
    createdAt: existingIdx >= 0 ? config.sets[existingIdx].createdAt : now,
    updatedAt: now,
    citizenship: params.meta,
  };

  if (existingIdx >= 0) config.sets[existingIdx] = set;
  else config.sets.push(set);

  if (params.meta.kind === 'import') config.importSetId = set.id;
  if (params.meta.kind === 'export') config.exportSetId = set.id;

  const { error } = await saveFieldMappingConfig(params.workspaceId, CSV_CONNECTOR_TYPE, config);
  if (error) return { profile: { set, meta: params.meta }, error };

  await syncAmbientScheduleForProfile(params.workspaceId, set.id, params.meta);

  return { profile: { set, meta: params.meta }, error: null };
}

export async function deleteCsvCitizenshipProfile(
  workspaceId: string,
  profileId: string,
): Promise<{ error: string | null }> {
  const config = await loadFieldMappingConfig(workspaceId, CSV_CONNECTOR_TYPE);
  const next = config.sets.filter((s) => s.id !== profileId);
  if (next.length === config.sets.length) return { error: 'Profile not found' };
  config.sets = next;
  if (config.importSetId === profileId) config.importSetId = null;
  if (config.exportSetId === profileId) config.exportSetId = null;
  const { error } = await saveFieldMappingConfig(workspaceId, CSV_CONNECTOR_TYPE, config);
  if (!error) {
    await disableAmbientForProfile(workspaceId, profileId);
  }
  return { error };
}

/** Persist last uploaded file on the profile for scheduled re-runs. */
export async function storeCsvProfileUpload(params: {
  workspaceId: string;
  profileId: string;
  fileName: string;
  fileText: string;
}): Promise<{ error: string | null }> {
  const profile = await getCsvCitizenshipProfile(params.workspaceId, params.profileId);
  if (!profile) return { error: 'Profile not found' };
  if (profile.meta.kind !== 'import') return { error: 'Not an import profile' };
  if (params.fileText.length > 2_000_000) {
    return { error: 'File too large to store for scheduled runs (max ~2MB). Use a URL source.' };
  }
  const meta: CsvCitizenshipProfileMeta = {
    ...profile.meta,
    source: 'upload',
    lastFileText: params.fileText,
    lastFileName: params.fileName,
    lastFileStoredAt: new Date().toISOString(),
  };
  const { error } = await saveCsvCitizenshipProfile({
    workspaceId: params.workspaceId,
    name: profile.set.name,
    description: profile.set.description,
    meta,
    entries: profile.set.entries,
    setId: profile.set.id,
  });
  return { error };
}

async function syncAmbientScheduleForProfile(
  workspaceId: string,
  profileId: string,
  meta: CsvCitizenshipProfileMeta,
): Promise<void> {
  const jobType = meta.kind === 'import' ? 'csv_import' : 'csv_export';
  const cadence = meta.scheduleCadenceMinutes ?? null;
  const supabase = getSupabaseClient();

  const { data: existing } = await supabase
    .from('ambient_jobs')
    .select('id, config')
    .eq('workspace_id', workspaceId)
    .eq('job_type', jobType)
    .maybeSingle();

  if (cadence == null || cadence <= 0) {
    // Manual-only: keep job but disable if this profile was the bound one
    if (existing && (existing.config as { profileId?: string } | null)?.profileId === profileId) {
      await supabase
        .from('ambient_jobs')
        .update({
          enabled: false,
          config: { ...(existing.config as object), profileId },
        })
        .eq('id', existing.id);
    }
    return;
  }

  const nextRun = new Date(Date.now() + cadence * 60_000).toISOString();
  if (existing) {
    await supabase
      .from('ambient_jobs')
      .update({
        enabled: true,
        cadence_minutes: cadence,
        next_run_at: nextRun,
        config: { ...(existing.config as object), profileId },
      })
      .eq('id', existing.id);
  } else {
    await supabase.from('ambient_jobs').insert({
      workspace_id: workspaceId,
      job_type: jobType,
      enabled: true,
      cadence_minutes: cadence,
      next_run_at: nextRun,
      config: { profileId },
    });
  }
}

async function disableAmbientForProfile(workspaceId: string, profileId: string): Promise<void> {
  const supabase = getSupabaseClient();
  const { data: jobs } = await supabase
    .from('ambient_jobs')
    .select('id, config, job_type')
    .eq('workspace_id', workspaceId)
    .in('job_type', ['csv_import', 'csv_export']);
  for (const job of jobs ?? []) {
    if ((job.config as { profileId?: string } | null)?.profileId === profileId) {
      await supabase.from('ambient_jobs').update({ enabled: false }).eq('id', job.id);
    }
  }
}

/** Default import mapping entries (CSV header → internal field). */
export function defaultImportEntries(headers: string[]): FieldMappingEntry[] {
  const now = Date.now();
  const auto: Record<string, string> = {
    title: 'title',
    name: 'title',
    description: 'description',
    external_id: 'external_id',
    externalid: 'external_id',
    id: 'id',
    category: 'itemCategory',
    itemcategory: 'itemCategory',
    altitude: 'altitudeLevel',
    altitudelevel: 'altitudeLevel',
  };
  return headers.map((h, i) => {
    const key = h.trim();
    const norm = key.toLowerCase().replace(/\s+/g, '');
    return {
      id: `e_${now}_${i}`,
      externalFieldKey: key,
      externalFieldLabel: key,
      internalFieldKey: auto[norm] ?? (norm === 'title' ? 'title' : key),
      direction: 'import' as const,
      enabled: true,
      valueMap: [],
    };
  });
}

export function defaultExportEntries(columnOrder: string[]): FieldMappingEntry[] {
  const now = Date.now();
  return columnOrder.map((internal, i) => ({
    id: `ex_${now}_${i}`,
    externalFieldKey: internal,
    externalFieldLabel: internal,
    internalFieldKey: internal,
    direction: 'export' as const,
    enabled: true,
    valueMap: [],
  }));
}
